import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { LobbyAccountStatusNotice } from "./LobbyAccountStatusNotice.jsx";
import BetaWelcomeCard from "./BetaWelcomeCard";
import V2DesignPrompt from "./V2DesignPrompt";
import { Box, Button, Alert, AlertTitle, Typography } from "@mui/material";
import { colors } from "../../theme/colors";

function formatUtcHourLocal(utcHour) {
  if (utcHour == null) return "";
  const parsedUtcHour =
    typeof utcHour === "number"
      ? utcHour
      : Number.parseInt(String(utcHour).split(":")[0], 10);
  if (!Number.isFinite(parsedUtcHour)) return "";
  const d = new Date();
  d.setUTCHours(parsedUtcHour, 0, 0, 0);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function LobbyAlerts({
  queueClosedInfo,
  discordInviteUrl,
  scheduleCountdown,
  user,
  actions,
  founderProgress,
  referralCode,
  onCopyReferralLink,
  lobbyNoticeRefreshKey,
  isAccountAllowedToPlay,
}) {
  const { accountStatus = null } = user || {};
  const schedule = queueClosedInfo?.schedule;
  const localOpenTime = formatUtcHourLocal(schedule?.opensAtUTC);
  const localCloseTime = formatUtcHourLocal(schedule?.closesAtUTC);
  const isScheduleClosed = schedule?.enabled && !schedule?.currentlyOpen;
  const isAdminClosed =
    queueClosedInfo?.queueClosed &&
    (!schedule?.enabled || schedule?.currentlyOpen);
  const isScheduleOpenWithTimer =
    schedule?.enabled && schedule?.currentlyOpen;

  return (
    <Box 
        className="alertContainer"
        sx={{
            position: "relative",
            zIndex: 10,
            width: "100%",
            margin: "0 auto",
        }}
    >
      {isAdminClosed && (
        <Alert severity="info">
          <AlertTitle>Matchmaking queue is temporarily closed</AlertTitle>
          You can’t start searching for a match right now. Please try again
          later.
        </Alert>
      )}

      {isScheduleClosed && (
        <Alert
          severity="info"
          sx={{
            marginBottom: 2,
            backgroundColor: colors.background.base,
            border: `1px solid ${colors.border.default}`,
            "& .MuiAlert-icon": { color: colors.primary.main },
          }}
        >
          <AlertTitle sx={{ fontWeight: 700, color: colors.text.primary }}>
            Matchmaking opens at {localOpenTime}
            {scheduleCountdown ? ` (in ${scheduleCountdown})` : ""}
          </AlertTitle>
          <Typography
            variant="body2"
            sx={{ color: colors.text.secondary, mb: 1 }}
          >
            During the beta we concentrate matchmaking into peak hours so you
            find games faster.
            {schedule?.geoQueueName
              ? ` Your region (${schedule.geoQueueName}) plays daily from ${localOpenTime} to ${localCloseTime}.`
              : ` Queue hours are ${localOpenTime} – ${localCloseTime} daily.`}
          </Typography>
          <Button
            variant="contained"
            size="small"
            href={discordInviteUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              backgroundColor: colors.primary.main,
              color: colors.text.primary,
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "none",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: colors.primary.active,
                boxShadow: "none",
              },
            }}
          >
            Join Discord to coordinate with other players
          </Button>
        </Alert>
      )}

      {isScheduleOpenWithTimer && !queueClosedInfo?.queueClosed && (
        <Typography
          sx={{
            color: colors.state.success.main,
            fontSize: "0.88rem",
            fontWeight: 600,
            mb: 0.5,
          }}
        >
          Queue open until {localCloseTime}
        </Typography>
      )}

      <LobbyAccountStatusNotice
        accountStatus={accountStatus}
        phoneVerificationStatus={user?.phoneVerification?.status ?? null}
        readinessRefreshKey={lobbyNoticeRefreshKey}
        user={user}
      />

      <V2DesignPrompt user={user} actions={actions} />

      {/* {founderProgress?.spotsAvailable && !founderProgress?.userIsFounder && (
        <Alert
          severity="info"
          sx={{
            backgroundColor: colors.background.base,
            border: `1px solid ${colors.border.default}`,
            "& .MuiAlert-icon": { color: colors.state.warning.main },
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: colors.text.primary }}
          >
            <strong>{founderProgress.remaining}</strong> Founder spots remaining
            —{" "}
            <Link to="/membership/plans" style={{ color: colors.state.warning.main }}>
              Subscribe to claim yours
            </Link>
          </Typography>
        </Alert>
      )} */}

      {/* {isAccountAllowedToPlay && (
        <BetaWelcomeCard
          discordInviteUrl={discordInviteUrl}
          onInviteFriend={referralCode ? onCopyReferralLink : null}
          schedule={schedule}
          localOpenTime={localOpenTime}
          localCloseTime={localCloseTime}
          isQueueOpen={
            isScheduleOpenWithTimer && !queueClosedInfo?.queueClosed
          }
        />
      )} */}
    </Box>
  );
}

LobbyAlerts.propTypes = {
  queueClosedInfo: PropTypes.object,
  discordInviteUrl: PropTypes.string,
  scheduleCountdown: PropTypes.string,
  user: PropTypes.object,
  actions: PropTypes.object,
  founderProgress: PropTypes.object,
  referralCode: PropTypes.string,
  onCopyReferralLink: PropTypes.func,
  lobbyNoticeRefreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isAccountAllowedToPlay: PropTypes.bool,
};
